import { Capacitor, type PluginListenerHandle } from '@capacitor/core';
import { alertLocationFromCoordinates, unavailableAlertLocation } from '../shared/alertLocation';
import { minimalAlertLocation, validInstallationId, validRegistrationSecret, type NativePushRegistration } from '../shared/nativePush';
import type { AlertLocation, AlertPreferences, AppConfig } from '../shared/types';
import { getApiJson, isNativeApp, postNativeApiJson } from './platform';

const STORAGE_KEY = 'stone-harbor-native-alert-registration-v1';
type SavedRegistration = NativePushRegistration & { enabled: boolean; pendingRemoval?: boolean };
let operation: Promise<unknown> = Promise.resolve();
function serialize<T>(action: () => Promise<T>): Promise<T> {
  const result = operation.then(action, action);
  operation = result.catch(() => undefined);
  return result;
}

function readRegistration(): SavedRegistration | undefined {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    return validInstallationId(saved?.installationId) && validRegistrationSecret(saved?.secret) &&
      Number.isSafeInteger(saved?.revision) && saved.revision > 0 && typeof saved?.token === 'string' ? saved : undefined;
  } catch { return undefined; }
}

function randomHex(): string {
  return [...crypto.getRandomValues(new Uint8Array(32))].map(v => v.toString(16).padStart(2, '0')).join('');
}

async function currentLocation(): Promise<AlertLocation> {
  if (document.visibilityState !== 'visible') return unavailableAlertLocation('Open the app to confirm your area. Local push alerts are paused.');
  const { Geolocation } = await import('@capacitor/geolocation');
  try {
    if (document.visibilityState !== 'visible') return unavailableAlertLocation('Open the app to confirm your area. Local push alerts are paused.');
    const position = await Geolocation.getCurrentPosition({ enableHighAccuracy: false, maximumAge: 600_000, timeout: 8_000 });
    return minimalAlertLocation(alertLocationFromCoordinates(position.coords.latitude, position.coords.longitude))!;
  } catch {
    return unavailableAlertLocation('Location was not confirmed. Local push alerts are paused.');
  }
}

export function saveNativeAlertPreferences(preferences: AlertPreferences): Promise<AlertLocation | undefined> {
  return serialize(() => updatePreferences(preferences));
}

async function updatePreferences(preferences: AlertPreferences): Promise<AlertLocation | undefined> {
  if (!isNativeApp()) throw new Error('Native alerts require the installed app');
  const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
  let saved = readRegistration();
  if (!Object.values(preferences).some(Boolean)) {
    // Remove the server subscription before forgetting its revocation credential.
    if (saved) {
      saved = { ...saved, revision: saved.revision + 1, preferences, enabled: false, pendingRemoval: true };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(saved));
      await postNativeApiJson('/api/native-push/unregister', { installationId: saved.installationId, secret: saved.secret, revision: saved.revision });
    }
    await FirebaseMessaging.deleteToken();
    // Retain only the capability and monotonic revision for safe future re-enabling.
    if (saved) localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...saved, token: '', location: undefined, pendingRemoval: false }));
    return undefined;
  }
  const config = await getApiJson<AppConfig>('/api/config');
  if (!config.nativePush?.configured) throw new Error('Background alerts are temporarily unavailable. Please try again later.');
  let permissions = await FirebaseMessaging.checkPermissions();
  if (permissions.receive === 'prompt' || permissions.receive === 'prompt-with-rationale') permissions = await FirebaseMessaging.requestPermissions();
  if (permissions.receive !== 'granted') throw new Error('Allow notifications in your phone settings to enable background alerts.');
  const location = await currentLocation();
  if (Capacitor.getPlatform() === 'android') await FirebaseMessaging.createChannel({
    id: 'stone-harbor-alerts', name: 'Stone Harbor alerts', description: 'Your selected local beach and weather alerts', importance: 4, visibility: 1,
  });
  const { token } = await FirebaseMessaging.getToken();
  saved = {
    installationId: saved?.installationId || randomHex(), secret: saved?.secret || randomHex(), token,
    revision: (saved?.revision || 0) + 1,
    platform: Capacitor.getPlatform() === 'ios' ? 'ios' : 'android', preferences, location, enabled: true,
  };
  // Save the owner credential before the network call, so uncertain writes can retry safely.
  localStorage.setItem(STORAGE_KEY, JSON.stringify(saved));
  await postNativeApiJson('/api/native-push/register', saved);
  return location;
}

export async function watchNativeAlerts(onOpen: () => void): Promise<() => void> {
  if (!isNativeApp()) return () => {};
  const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
  const handles: PluginListenerHandle[] = [];
  let closed = false;
  const update = (token?: string) => {
    void serialize(async () => {
      const saved = readRegistration();
      if (closed || !saved) return;
      if (saved.pendingRemoval) {
        await postNativeApiJson('/api/native-push/unregister', { installationId: saved.installationId, secret: saved.secret, revision: saved.revision });
        await FirebaseMessaging.deleteToken();
        if (readRegistration()?.revision === saved.revision) localStorage.setItem(STORAGE_KEY,
          JSON.stringify({ ...saved, token: '', location: undefined, pendingRemoval: false }));
        return;
      }
      if (!saved.enabled) return;
      if (document.visibilityState !== 'visible' && !token) return;
      const permission = await FirebaseMessaging.checkPermissions();
      if (permission.receive !== 'granted') {
        const revision = saved.revision + 1;
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...saved, revision, enabled: false, pendingRemoval: true }));
        await postNativeApiJson('/api/native-push/unregister', { installationId: saved.installationId, secret: saved.secret, revision });
        if (readRegistration()?.revision === revision) localStorage.setItem(STORAGE_KEY,
          JSON.stringify({ ...saved, revision, enabled: false, pendingRemoval: false, token: '', location: undefined }));
        return;
      }
      let location = saved.location;
      if (document.visibilityState === 'visible') {
        const { Geolocation } = await import('@capacitor/geolocation');
        const locationPermission = await Geolocation.checkPermissions();
        const allowed = locationPermission.location === 'granted' || locationPermission.coarseLocation === 'granted';
        location = allowed ? await currentLocation() : unavailableAlertLocation('Location permission is off. Local push alerts are paused.');
      }
      const current = { ...saved, token: token || (await FirebaseMessaging.getToken()).token, location, revision: saved.revision + 1 };
      const latest = readRegistration();
      if (closed || !latest?.enabled || latest.revision !== saved.revision) return;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(current));
      await postNativeApiJson('/api/native-push/register', current);
    }).catch(() => { /* Keep saved credentials and retry on the next foreground event. */ });
  };
  handles.push(await FirebaseMessaging.addListener('tokenReceived', event => update(event.token)));
  handles.push(await FirebaseMessaging.addListener('notificationActionPerformed', () => onOpen()));
  const onVisible = () => { if (document.visibilityState === 'visible') update(); };
  document.addEventListener('visibilitychange', onVisible);
  update();
  return () => { closed = true; document.removeEventListener('visibilitychange', onVisible); handles.forEach(h => { void h.remove(); }); };
}
